/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import Ladda from 'ladda';

import showErrorMessage from '@/util/showErrorMessage';
import RegionGroups from '@/enums/RegionGroups';

import GetInternalUserRegions from '../../services/GetInternalUserRegions';
import GetInternalUserRegionGroups from '../../services/GetInternalUserRegionGroups';
import GetInternalUserRoles from '../../services/GetInternalUserRoles';
import SearchUsersBtnView from '../SearchUsersBtnView/SearchUsersBtnView';
import SelectionCollectionView from '../SelectionCollectionView/SelectionCollectionView';
import UserInfoView from '../UserInfoView/UserInfoView';
import UpdateUserRoles from '../../services/UpdateUserRoles';
import UpdateUserRegions from '../../services/UpdateUserRegions';
import UpdateUserRegionGroups from '../../services/UpdateUserRegionGroups';

import tmplUserAccessView from './UserAccessView.hbs';

const UserAccessView = View.extend({
  template: tmplUserAccessView,

  ui: {
    btnSave: '.btn-save',
    regionSelectionCollectionView: '.region-selection-collectionview',
    regionGroupSelectionCollectionView: '.regiongroup-selection-collectionview',
    roleSelectionCollectionView: '.role-selection-collectionview',
    searchUsersBtnView: '.search-users-btn-view',
    userInfoView: '.user-info-view'
  },

  regions: {
    regionSelectionCollectionView: '@ui.regionSelectionCollectionView',
    regionGroupSelectionCollectionView: '@ui.regionGroupSelectionCollectionView',
    roleSelectionCollectionView: '@ui.roleSelectionCollectionView',
    searchUsersBtnView: '@ui.searchUsersBtnView',
    userInfoView: '@ui.userInfoView'
  },

  events: {
    'click @ui.btnSave': 'onSave'
  },

  childViewEvents: {
    'user:selected': 'onUserSelected'
  },

  initialize() {
    this.selectedUser = null;
  },

  onRender() {
    this.ui.btnSave.hide();
    this.showSearchUsersBtnView();
    this.showRoleSelectionCollectionView();
    this.showRegionSelectionCollectionView();
    this.showRegionGroupSelectionCollectionView();
  },

  showSearchUsersBtnView() {
    const searchUsersBtnView = new SearchUsersBtnView();
    this.showChildView('searchUsersBtnView', searchUsersBtnView);
  },

  showUserInfoView(user) {
    const model = new Backbone.Model(user);
    const userInfoView = new UserInfoView({ model });
    this.showChildView('userInfoView', userInfoView);
  },

  showRoleSelectionCollectionView() {
    const roles = this.model.get('roles');
    const collectionItems = _.map(roles, (role) => ({ ...role, label: role.userType }));
    const collection = new Backbone.Collection(collectionItems);
    const selectionCV = new SelectionCollectionView({ collection });
    this.showChildView('roleSelectionCollectionView', selectionCV);
  },

  showRegionSelectionCollectionView() {
    const regions = this.model.get('regions');
    const collectionItems = _.map(regions, (region) => ({ ...region, label: region.name }));
    const collection = new Backbone.Collection(collectionItems);
    const selectionCV = new SelectionCollectionView({ collection });
    this.showChildView('regionSelectionCollectionView', selectionCV);
  },

  showRegionGroupSelectionCollectionView() {
    const regionGroups = this.model.get('regionGroups');
    const collectionItems = _.map(regionGroups, (group) => ({ ...group, label: group.regionGroup }));
    const collection = new Backbone.Collection(collectionItems);
    const selectionCV = new SelectionCollectionView({ collection });

    selectionCV.on('checkbox:changed', (model) => this.onRegionGroupCheckboxChanged(model));

    this.showChildView('regionGroupSelectionCollectionView', selectionCV);
  },

  showInvalidUserError() {
    showErrorMessage({
      errorCode: 'invalidUserError',
      errorTitle: 'Invalid User',
      errorMessage: 'The current selected user is invalid. Please try again.'
    });
  },

  showSaveError() {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage: 'An error occurred while updating the user\'s roles, regions, and/or region groups. Please try again.'
    });
  },

  showUserInfoError() {
    showErrorMessage({
      errorCode: 'userInfoError',
      errorTitle: 'User Info Error',
      errorMessage: 'An error occurred while retrieving user information. Please try again.'
    });
  },

  async onSave() {
    const userId = this.selectedUser.id;
    if (!userId) {
      this.showInvalidUserError();
      return;
    }

    const roles = this.getChildView('roleSelectionCollectionView').getChecked();
    const roleIds = this.getRoleIds(roles);

    const regions = this.getChildView('regionSelectionCollectionView').getChecked();
    const regionIds = this.getRegionIds(regions);

    const regionGroups = this.getChildView('regionGroupSelectionCollectionView').getChecked();
    const regionGroupIds = this.getRegionGroupIds(regionGroups);

    const spinner = Ladda.create(this.ui.btnSave[0]);
    spinner.start();

    try {
      await Promise.all([
        UpdateUserRoles(userId, roleIds),
        UpdateUserRegions(userId, regionIds),
        UpdateUserRegionGroups(userId, regionGroupIds)
      ]);
    } catch (err) {
      this.showSaveError();
    } finally {
      spinner.stop();
    }
  },

  async onUserSelected(user) {
    try {
      this.getChildView('searchUsersBtnView')?.laddaStart();

      const [roles, regions, regionGroups] = await this.getUserData(user.id);

      this.selectedUser = user;
      this.showUserInfoView(user);

      const roleIds = this.getRoleIds(roles);
      const regionIds = this.getRegionIds(regions);
      const regionGroupIds = this.getRegionGroupIds(regionGroups);

      this.showRoleSelectionCollectionView();
      this.getChildView('roleSelectionCollectionView')?.setChecked(roleIds);

      this.showRegionSelectionCollectionView();
      this.getChildView('regionSelectionCollectionView')?.setChecked(regionIds);

      this.showRegionGroupSelectionCollectionView();
      this.getChildView('regionGroupSelectionCollectionView')?.setChecked(regionGroupIds);

      this.ui.btnSave.show();
    } catch (err) {
      this.showUserInfoError();
      return;
    } finally {
      this.getChildView('searchUsersBtnView')?.laddaStop();
    }
  },

  async getUserData(userId) {
    return await Promise.all([
      GetInternalUserRoles(userId),
      GetInternalUserRegions(userId),
      GetInternalUserRegionGroups(userId)
    ]);
  },

  onRegionGroupCheckboxChanged(model) {
    const regionIds = model.get('regionIds');

    if (model?.get('id') === RegionGroups.ALL) {
      if (model.get('isChecked')) {
        this.setAllRegionGroupsSelected();
      } else {
        this.setAllRegionGroupsUnselected();
      }
    }

    if (model.get('isChecked')) {
      this.setRegionsSelected(regionIds);
    } else {
      const regionIdsToDeselect = this.filterUnselectedRegionIds(regionIds);
      this.setRegionsUnselected(regionIdsToDeselect);
    }
  },

  getRoleIds(roles) {
    return _.map(roles, (role) => (role instanceof Backbone.Model ? role.get('id') : role.roleId));
  },

  getRegionIds(regions) {
    return _.map(regions, (region) => (region instanceof Backbone.Model ? region.get('id') : region.regionId));
  },

  getRegionGroupIds(regionGroups) {
    return _.map(regionGroups, (regionGroup) =>
      regionGroup instanceof Backbone.Model ? regionGroup.get('id') : regionGroup.regionGroupId
    );
  },

  getAllSelectedGroupsRegionIds() {
    const selectedRegionGroups = this.getChildView('regionGroupSelectionCollectionView').getChecked();
    return _.flatten(_.map(selectedRegionGroups, (group) => group.get('regionIds')));
  },

  filterUnselectedRegionIds(regionIds) {
    const selectedRegionGroupIds = this.getAllSelectedGroupsRegionIds();
    return _.filter(regionIds, (regionId) => !_.includes(selectedRegionGroupIds, regionId));
  },

  setAllRegionGroupsSelected() {
    this.getChildView('regionGroupSelectionCollectionView')?.setAllChecked();
  },

  setAllRegionGroupsUnselected() {
    this.getChildView('regionGroupSelectionCollectionView')?.setAllUnchecked();
  },

  setRegionsSelected(regionIds) {
    this.getChildView('regionSelectionCollectionView')?.setChecked(regionIds);
  },

  setRegionsUnselected(regionIds) {
    this.getChildView('regionSelectionCollectionView')?.setUnchecked(regionIds);
  }
});

export default UserAccessView;
